import {
  type SocialPlatformBaseSettings,
  type SocialPlatformComputedSettings,
} from "./types";

// Import base settings from individual files
import { blueskyBaseSettings } from "./bluesky";
import { facebookBaseSettings } from "./facebook";
import { googlebusinessBaseSettings } from "./googlebusiness";
import { instagramBaseSettings } from "./instagram";
import { linkedinBaseSettings } from "./linkedin";
import { pinterestBaseSettings } from "./pinterest";
import { redditBaseSettings } from "./reddit";
import { telegramBaseSettings } from "./telegram";
import { threadsBaseSettings } from "./threads";
import { tiktokBaseSettings } from "./tiktok";
import { twitterBaseSettings } from "./twitter";
import { youtubeBaseSettings } from "./youtube";

// Aggregate base settings
export const socialPlatformBases: Record<string, SocialPlatformBaseSettings> = {
  facebook: facebookBaseSettings,
  instagram: instagramBaseSettings,
  twitter: twitterBaseSettings,
  linkedin: linkedinBaseSettings,
  pinterest: pinterestBaseSettings,
  tiktok: tiktokBaseSettings,
  youtube: youtubeBaseSettings,
  threads: threadsBaseSettings,
  googlebusiness: googlebusinessBaseSettings,
  bluesky: blueskyBaseSettings,
  reddit: redditBaseSettings,
  telegram: telegramBaseSettings,
};

// Helper function to get computed settings for a specific platform and post type
export const getSocialPlatformSettings = (
  platformId: string,
  // Determine a sensible default post type - 'post' seems common,
  // but Pinterest uses 'pin', Reddit has multiple, Telegram 'message'
  // Let's stick with 'post' for now, requiring callers to be specific if needed.
  postTypeId: string = "post"
): SocialPlatformComputedSettings | undefined => {
  const base = socialPlatformBases[platformId.toLowerCase()];
  if (!base) {
    return undefined;
  }

  // Find the specific post type settings, using an empty object as fallback ONLY if the requested type is the common default 'post'.
  // For specific types like 'reel', 'story', 'article', 'pin', 'short', 'text_post' etc., they MUST exist in the definition.
  let postTypeSpecifics = base.postTypes[postTypeId];
  if (
    !postTypeSpecifics &&
    (postTypeId === "post" ||
      postTypeId === "pin" ||
      postTypeId === "message" ||
      postTypeId === "update")
  ) {
    // If a common default type ID wasn't found explicitly, assume no overrides.
    postTypeSpecifics = {};
  } else if (!postTypeSpecifics) {
    // A specific, non-default post type was requested but not defined for this platform.
    console.warn(
      `Post type '${postTypeId}' not explicitly defined for platform '${platformId}'.`
    );
    return undefined;
  }

  // Define default fields that are 'not_allowed' unless specified otherwise
  const defaultFields: SocialPlatformComputedSettings["fields"] = {
    title: "not_allowed",
    caption: "not_allowed",
    location: "not_allowed",
    altText: "not_allowed",
    firstComment: "not_allowed",
    linkAttachment: "not_allowed",
    polls: "not_allowed",
    userTags: "not_allowed",
  };

  // Merge fields: start with defaults, override with base, then with postType specifics
  const mergedFields = {
    ...defaultFields,
    ...base.fields,
    ...postTypeSpecifics?.fields,
  };

  // Merge limits: start with base, override with postType specifics
  const mergedLimits = {
    ...base.limits,
    ...postTypeSpecifics?.limits,
  };

  // Merge notes: combine base and postType notes
  const mergedNotes = [
    ...(base.notes ?? []),
    ...(postTypeSpecifics?.notes ?? []),
  ];

  // Construct the computed name
  // Basic heuristic: if suffix exists, use it. If not, and it's not a common default name, capitalize and append typeId.
  const computedName = `${base.name}${
    postTypeSpecifics?.nameSuffix ??
    (!["post", "pin", "message", "update"].includes(postTypeId)
      ? ` ${postTypeId.charAt(0).toUpperCase() + postTypeId.slice(1).replace("_", " ")}`
      : "")
  }`;

  return {
    platformId: platformId.toLowerCase(),
    postTypeId: postTypeId,
    name: computedName,
    icon: base.icon,
    fields: mergedFields,
    limits: mergedLimits,
    notes: mergedNotes.length > 0 ? mergedNotes : undefined,
  };
};

// Function to get available post types for a platform
export const getAvailablePostTypes = (
  platformId: string
): { id: string; name: string }[] => {
  const base = socialPlatformBases[platformId.toLowerCase()];
  if (!base) {
    return [];
  }

  return Object.entries(base.postTypes).map(([id, settings]) => ({
    id,
    // Construct the name similarly to the main helper function
    name: `${base.name}${
      settings?.nameSuffix ??
      (!["post", "pin", "message", "update"].includes(id)
        ? ` ${id.charAt(0).toUpperCase() + id.slice(1).replace("_", " ")}`
        : "")
    }`,
  }));
};
